import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Poolcorp Custom Code */
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { canAddToCart } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import ProductContextAvailability from "@store/Overrides/Components/ProductContextAvailability";
import { contentSpacingStyles, editLinkStyles } from "@store/theme/common/mixins";
import { TYPOGRAPHY } from "@store/theme/design";
import getBooleanProperty from "@store/utilities/getBooleanProperty";
import parseNumber from "@store/utilities/parseNumber";
import Icon from "@insite/mobius/Icon";
import { getStoreSession } from "@store/Store/Context/ContextSelectors";
import AlertFill from "@store/Components/Icons/AlertFill";
import Color from "color";
import StorePickupRounded from "@store/Components/Icons/StorePickupRounded";
import DeliveryTruckRounded from "@store/Components/Icons/DeliveryTruckRounded";
import { isAuthenticatedOrRemembered } from "@store/utilities/isAuthenticatedOrRemembered";
/* Poolcorp Custom Code */

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;
    // Poolcorp Custom Code
    showAvailability: boolean;
}

const mapStateToProps = (state: ApplicationState, props: HasProductContext) => ({
    productSettings: getSettingsCollection(state).productSettings,
    // Poolcorp Custom Code
    canAddToCart: canAddToCart(state, props.productContext?.product),
    hasEnoughInventory: props.productContext ? hasEnoughInventory(state, props.productContext) : false,
    fulfillmentMethod: getStoreSession(state).fulfillmentMethod,
});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    // Poolcorp Custom Code
    highDemandModeText?: TypographyPresentationProps;
    fulfillmentMethodWrapper?: InjectableCss;
    fulfillmentMethodText?: TypographyPresentationProps;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            justify-content: space-between;
            flex-wrap: wrap;
            flex-direction: column;
            align-items: flex-start;
            ${contentSpacingStyles("0.25rem", null)}
        `,
    },
    unitOfMeasureSelect: {
        sizeVariant: "small",
        labelProps: {
            size: TYPOGRAPHY.size.smallest,
            css: css`
                margin-right: 0.5rem;
                width: auto;
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-top: 0.4rem;
        `,
    },
    addToCartButton: {},
    quantityOrdered: {
        cssOverrides: {
            formInputWrapper: css`
                margin-right: 0.8rem;
            `,
        },
    },
    addToListWrapper: {
        css: css`
            border-top: 1px solid ${getColor("common.border")};
            margin-top: 1rem;
            min-height: 2rem;
            padding-top: 1rem;
            text-align: right;
            width: 100%;
        `,
    },
    addToListLink: {
        link: {
            css: css`
                ${editLinkStyles()}
            `,
        },
    },
    price: {
        wrapper: {},
    },
    highDemandModeText: {
        color: "text.accent",
        italic: true,
        size: TYPOGRAPHY.size.smallest,
        weight: TYPOGRAPHY.weight.bold,
        css: css`
            margin-right: 0.25rem;
            ${wrapInContainerStyles}
        `,
    },
    fulfillmentMethodWrapper: {
        css: css`
            display: flex;
            width: calc(100% + 2.5rem);
            padding: .75rem 1.25rem;
            margin: 1rem -1.25rem 0;
            align-items: center;

            ${props => {
                return css`
                    background-color: ${Color(props.theme.colors.primary.main).lighten(0.83).hex()};
                `;
            }}
        `,
    },
    fulfillmentMethodText: {
        weight: TYPOGRAPHY.weight.bold,
        color: "primary",
        size: TYPOGRAPHY.size.base,

        css: css`
            margin-left: .5rem;
        `,
    },
};

const styles = actionsStyles;

const ProductListActions: FC<Props> = ({
    productContext,
    productSettings,
    showPrice,
    showAddToList,
    // Poolcorp Custom Code
    showAvailability,
    canAddToCart,
    hasEnoughInventory,
    fulfillmentMethod,
}) => {
    /* Poolcorp Custom Code */
    if (!isAuthenticatedOrRemembered()) {
        return null;
    }

    const isHighDemandMode = getBooleanProperty(productContext?.product?.properties?.isHighDemandMode);
    const parsedQtyLimit = parseNumber(productContext?.product?.properties?.qtyLimit);
    const qtyLimit = parsedQtyLimit || -1;
    const highDemandMessage = isHighDemandMode
        ? `${translate(qtyLimit >= 0 ? "Max. {0} per order" : "Limited quantities", `${qtyLimit}`)}`
        : "";

    const getFulfillmentMethodDisplay = (FulfillmentMethod: string) => {
        switch (FulfillmentMethod) {
            case "Ship":
                return {fulfillmentMethodText: "For Delivery", iconSrc: DeliveryTruckRounded, iconSize: 32};
            case "PickUp":
                return {fulfillmentMethodText: "For Pickup", iconSrc: StorePickupRounded, iconSize: 32};
            default:
                return {fulfillmentMethodText: "Fullfilment Method Not Available", iconSrc: AlertFill, iconSize: 30};
        }
    };

    const { fulfillmentMethodText, iconSrc, iconSize } = getFulfillmentMethodDisplay(fulfillmentMethod);
    /* Poolcorp Custom Code */

    return (
        <StyledWrapper {...styles.wrapper}>
            {productSettings.showInventoryAvailability && showAvailability && (
                <ProductContextAvailability 
                    isCondensed={false} // Poolcorp Custom Code
                    qtyOrdered={productContext.productInfo.qtyOrdered} // Poolcorp Custom Code
                />
            )}
            {showPrice && (
                <>
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={styles.price}
                    />
                    <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                </>
            )}
            {productSettings.canAddToCart && hasEnoughInventory && canAddToCart && (
                <>
                    <StyledWrapper {...styles.addToCartWrapper}>
                        <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.addToCartWrapper}>
                        <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} labelOverride="" />
                        <ProductAddToCartButton
                            data-test-selector={`actionsAddToCart${productContext.product.id}`}
                            extendedStyles={styles.addToCartButton}
                        />
                    </StyledWrapper>
                </>
            )}
            {/* Poolcorp Custom Code */}
            {isHighDemandMode && (
                <StyledWrapper {...styles.addToCartWrapper}>
                    <Typography {...styles.highDemandModeText}>{highDemandMessage}</Typography>
                </StyledWrapper>
            )}
            {fulfillmentMethod && (
                <StyledWrapper {...styles.fulfillmentMethodWrapper}> 
                    <Icon
                        src={iconSrc}
                        size={iconSize}
                        color={"primary"}
                    />
                    <Typography {...styles.fulfillmentMethodText}>{translate(fulfillmentMethodText)}</Typography>
                </StyledWrapper>
            )}
            {/* Poolcorp Custom Code */}
            {showAddToList && productContext.product.canAddToWishlist && (
                <StyledWrapper {...styles.addToListWrapper}>
                    <ProductAddToListLink
                        data-test-selector={`actionsAddToList${productContext.product.id}`}
                        extendedStyles={styles.addToListLink}
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductListActions)); // Poolcorp Custom Code
